import React, { useMemo, useState, useEffect } from 'react';
import { css } from '@emotion/core';
import {
  Heading,
  IconButton,
  Modal,
  PrimaryButton,
  Table,
  TableColumnConfig,
  Text,
  SyncIcon,
  useModalControl,
} from '@weave/design-system';
import { theme } from '@weave/theme';
import { Migration } from '@weave/schema-gen-ts/dist/schemas/organization/merge/coordinator/v1/api.pb';
import { listMigrations, listRegisteredServices } from './api';
import { getStateBackgroundColor } from './utils';
import { MigrationDetailsDrawer } from './migration-details-drawer';
import { RequestMigrationModal } from './request-migration-modal';
import { useAlert } from '@weave/alert-system';
import { useInfiniteQuery, useQuery } from 'react-query';
import axios, { AxiosError } from 'axios';

interface MigrationsTabProps {
  onRetryClick: (migrationId: string, isRollbackFailed: boolean) => void;
  retryRefetchTrigger: number;
  onRollbackClick: (migrationId: string) => void;
  rollbackRefetchTrigger: number;
}

export const MigrationsTab = ({
  onRetryClick,
  retryRefetchTrigger,
  onRollbackClick,
  rollbackRefetchTrigger,
}: MigrationsTabProps) => {
  const alert = useAlert();
  const { modalProps, triggerProps } = useModalControl();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedMigrationId, setSelectedMigrationId] = useState<string>('');

  const { data: registeredServicesData } = useQuery(
    ['org-merge-registered-services'],
    listRegisteredServices,
    { refetchOnWindowFocus: false }
  );
  const registeredServices = registeredServicesData?.services || [];

  const handleRowClick = (row: Migration) => {
    const id = row.migrationId || '';
    if (id) {
      setSelectedMigrationId(id);
      setIsDrawerOpen(true);
    }
  };

  const { data, fetchNextPage, hasNextPage, isFetching, refetch } = useInfiniteQuery(
    ['org-merge-migrations'],
    async ({ pageParam = '' }) => {
      const result = await listMigrations('25', pageParam);
      return result;
    },
    {
      getNextPageParam: (lastPage) => {
        return lastPage.nextPageToken ? lastPage.nextPageToken : undefined;
      },
      onError: (error: Error | AxiosError) => {
        console.error('Error fetching migrations:', error);
        if (axios.isAxiosError(error)) {
          alert.error('Failed to load migrations');
        } else {
          alert.error('An unexpected error occurred');
        }
      },
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  const handleRefresh = () => {
    refetch();
  };

  useEffect(() => {
    if (retryRefetchTrigger > 0 || rollbackRefetchTrigger > 0) {
      refetch();
    }
  }, [retryRefetchTrigger, rollbackRefetchTrigger, refetch]);

  const migrations = useMemo(() => {
    if (!data) return [];
    return data.pages.map((page) => page.migrations ?? []).flat();
  }, [data]);

  const columnConfig: TableColumnConfig<Migration>[] = [
    {
      Header: 'Migration ID',
      accessor: 'migrationId',
      id: 'migrationId',
      cellRenderer: (value: string, rowData?: Migration) => (
        <div
          role="button"
          aria-label="View migration details"
          tabIndex={0}
          onClick={() => rowData && handleRowClick(rowData)}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && rowData) {
              handleRowClick(rowData);
            }
          }}
          css={css`
            cursor: pointer;
            color: ${theme.colors.primary50};
            text-decoration: underline;

            &:hover {
              color: ${theme.colors.primary40};
            }

            &:focus {
              outline: 2px solid ${theme.colors.primary50};
              outline-offset: 2px;
            }
          `}
        >
          <Text>{value}</Text>
        </div>
      ),
      // @ts-ignore
      disableSortBy: true,
    },
    {
      Header: 'Source Org ID',
      accessor: 'sourceOrgId',
      id: 'sourceOrgId',
      cellRenderer: (value: string) => <Text>{value}</Text>,
      // @ts-ignore
      disableSortBy: true,
    },
    {
      Header: 'Destination Org ID',
      accessor: 'destinationOrgId',
      id: 'destinationOrgId',
      cellRenderer: (value: string) => <Text>{value}</Text>,
      // @ts-ignore
      disableSortBy: true,
    },
    {
      Header: 'State',
      accessor: 'state',
      id: 'state',
      cellRenderer: (value: string) => <Text>{value}</Text>,
      // @ts-ignore
      disableSortBy: true,
    },
    {
      Header: 'Created',
      accessor: 'createTime',
      id: 'createTime',
      cellRenderer: (value: string) => {
        if (!value) return <Text>-</Text>;
        const date = new Date(value);
        return <Text>{date.toLocaleString()}</Text>;
      },
      // @ts-ignore
      disableSortBy: true,
    },
    {
      Header: 'Completed',
      accessor: 'completionTime',
      id: 'completionTime',
      cellRenderer: (value: string) => {
        if (!value) return <Text>-</Text>;
        const date = new Date(value);
        return <Text>{date.toLocaleString()}</Text>;
      },
      // @ts-ignore
      disableSortBy: true,
    },
  ];

  return (
    <div>
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin: ${theme.spacing(2)} 0;
        `}
      >
        <div>
          <Heading level={2}>Migrations</Heading>
          <Text>
            List of org merge migrations. A migration represents a request to merge one
            org into another org.
          </Text>
        </div>
        <div css={headerActionsStyles}>
          <PrimaryButton {...triggerProps}>Request New Migration</PrimaryButton>
          <IconButton
            onClick={handleRefresh}
            disabled={isFetching}
            label="Refresh migrations"
          >
            <SyncIcon />
          </IconButton>
        </div>
      </div>
      <div style={{ marginTop: '1rem' }}>
        <Table
          data={migrations}
          colConfig={columnConfig}
          isLoading={isFetching}
          tableInstanceId="migrations-table"
          styleConfig={{
            rows: (rowData) =>
              css({
                backgroundColor: getStateBackgroundColor(rowData.state?.toString()),
              }),
          }}
        />
      </div>
      {hasNextPage && (
        <div css={loadMoreBtnContainer}>
          <PrimaryButton
            onClick={() => fetchNextPage()}
            disabled={isFetching}
            css={loadMoreButtonStyles}
          >
            Load More
          </PrimaryButton>
        </div>
      )}

      <MigrationDetailsDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        migrationId={selectedMigrationId}
        registeredServices={registeredServices}
        onRetryClick={onRetryClick}
        onRollbackClick={onRollbackClick}
      />

      <Modal {...modalProps}>
        <RequestMigrationModal
          onClose={modalProps.onClose}
          onSuccess={() => {
            modalProps.onClose();
            refetch();
          }}
        />
      </Modal>
    </div>
  );
};

const loadMoreBtnContainer = css`
  display: flex;
  justify-content: center;
  margin: ${theme.spacing(4, 0, 1)};
`;

const loadMoreButtonStyles = css`
  width: 300px;
`;

const headerActionsStyles = css`
  display: flex;
  gap: ${theme.spacing(2)};
  align-items: center;
`;
